import { localAgentSetting, sendKitchenToLocalAgent } from '../printing/local.js';
import { db } from './db.js';

/**
 * Kitchen tickets for orders sent while offline. The server's kitchen screen can't get them, so
 * they print from this device and, with "Offline kitchen screens" on (Settings → Offline &
 * displays), also show on:
 *   - the kitchen screen in another tab or window of this browser (BroadcastChannel + IndexedDB),
 *   - the print agent's kitchen screen on the shop's Wi-Fi, when this PC runs the print agent.
 */
const CHANNEL = 'shripos-kitchen';
const KEEP_MS = 12 * 3_600_000;

/**
 * One ticket per kitchen station, like the server's KOTs, for the quantities the kitchen doesn't
 * have yet (sentQty: lineId → qty already sent; includeNew false for a plain hold). Lines reduced
 * below what was sent give a cancellation ticket (sentLines: those lines as loaded).
 */
export function kitchenTicketsFor({ cartLines, sentQty = {}, sentLines = [], bill, catalog, me, includeNew = true }) {
  const items = new Map(catalog.items.map((i) => [i.id, i]));
  const billLines = new Map(bill.lines.map((l) => [l.lineId, l]));
  const groups = new Map();
  const add = (type, station, line) => {
    const key = `${type}:${station}`;
    if (!groups.has(key)) groups.set(key, { type, station, lines: [] });
    groups.get(key).lines.push(line);
  };
  const lineOf = (l, qty) => ({
    lineId: l.lineId,
    name: l.name,
    variantName: l.variantName ?? null,
    modifiers: (l.modifiers ?? []).map((m) => m.name ?? m),
    qty,
    note: l.note ?? null,
    foodType: l.foodType,
  });
  for (const c of includeNew ? cartLines : []) {
    const l = billLines.get(c.lineId);
    const sent = sentQty[c.lineId] ?? 0;
    if (!l || c.qty <= sent) continue;
    add('new', items.get(c.itemId)?.kitchenStation ?? 'kitchen', lineOf(l, c.qty - sent));
  }
  for (const [lineId, sent] of Object.entries(sentQty)) {
    const left = cartLines.find((c) => c.lineId === lineId)?.qty ?? 0;
    if (sent <= left) continue;
    const prev = sentLines.find((p) => p.lineId === lineId) ?? billLines.get(lineId);
    if (!prev) continue;
    add('cancel', items.get(prev.itemId)?.kitchenStation ?? 'kitchen', lineOf(prev, sent - left));
  }
  const stamp = Date.now().toString(36);
  return [...groups.values()].map((g, i) => ({
    id: `offline:${bill.clientUuid}:${stamp}:${i}`,
    kotNo: `${bill.tokenNo ?? ''}`,
    type: g.type,
    station: g.station,
    tokenNo: bill.tokenNo ?? null,
    tableNo: bill.tableNo ?? null,
    orderType: bill.orderType,
    lines: g.lines,
    status: 'new',
    createdAt: new Date().toISOString(),
    createdBy: me?.user?.name ?? null,
    offline: true,
  }));
}

let channel = null;
function kitchenChannel() {
  try {
    channel ??= new BroadcastChannel(CHANNEL);
  } catch {
    channel = null; // very old browsers: the kitchen tab still refreshes from IndexedDB
  }
  return channel;
}

/** Send offline tickets to the local kitchen screens. Never throws: tickets also print. */
export async function sendToLocalKitchen(tickets) {
  if (!tickets.length) return;
  try {
    await db.kitchen.bulkPut(tickets);
    kitchenChannel()?.postMessage({ type: 'changed' });
  } catch {
    /* storage blocked */
  }
  if (localAgentSetting().on) await sendKitchenToLocalAgent(tickets).catch(() => {});
}

/** Tickets from this browser, newest last; served ones stay for `recallMinutes` (recall). */
export async function localKitchenTickets({ station, recallMinutes = 2 } = {}) {
  try {
    const cutoff = Date.now() - KEEP_MS;
    await db.kitchen.where('createdAt').below(new Date(cutoff).toISOString()).delete();
    const all = await db.kitchen.orderBy('createdAt').toArray();
    const mine = station ? all.filter((t) => t.station === station) : all;
    const servedSince = Date.now() - recallMinutes * 60_000;
    return {
      tickets: mine.filter((t) => !['served', 'acknowledged'].includes(t.status)),
      recentlyServed: mine.filter((t) => t.status === 'served' && new Date(t.servedAt ?? 0).getTime() > servedSince),
    };
  } catch {
    return { tickets: [], recentlyServed: [] };
  }
}

export async function moveLocalTicket(id, status) {
  try {
    await db.kitchen.update(id, { status, ...(status === 'served' ? { servedAt: new Date().toISOString() } : {}) });
    kitchenChannel()?.postMessage({ type: 'changed' });
  } catch {
    /* ignore */
  }
}

/** Calls fn when another tab of this browser changed the local tickets. Returns an unsubscribe. */
export function onLocalKitchenChange(fn) {
  const ch = kitchenChannel();
  if (!ch) return () => {};
  const listener = (e) => e.data?.type === 'changed' && fn();
  ch.addEventListener('message', listener);
  return () => ch.removeEventListener('message', listener);
}

/**
 * Kitchen screen taps made offline and not uploaded yet, applied over the server's tickets so
 * the screen shows what the kitchen did (served / acknowledged tickets leave the list).
 */
export async function applyPendingKitchenTaps(data) {
  let taps;
  try {
    taps = await db.outbox.where('kind').equals('kot').toArray();
  } catch {
    return data;
  }
  if (!taps.length) return data;
  const byId = new Map(taps.map((o) => [o.clientUuid.slice('kot:'.length), o.payload]));
  const next = (t) => {
    const tap = byId.get(t.id);
    if (!tap) return t;
    return tap.path.endsWith('/ack') ? { ...t, status: 'acknowledged' } : { ...t, status: tap.body.status };
  };
  const all = [...data.tickets, ...data.recentlyServed].map(next);
  return {
    ...data,
    tickets: all.filter((t) => !['served', 'acknowledged'].includes(t.status)),
    recentlyServed: all.filter((t) => t.status === 'served'),
  };
}
